'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Plus, X, Hash, MessageSquare, Martini, Utensils } from 'lucide-react';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import type { AddCreateMenuItem } from '@utils/user-utils';

export function FloatingCreateMenu({
  actions
}: {
  actions: AddCreateMenuItem[];
}) {
  const [open, setOpen] = useState(false);
  const reducedMotion = useReducedMotion();
  const t = useTranslations('common');
  const label = actions.length === 1 ? actions[0].label : t('add-create');
  const trigger = (
    <button
      type="button"
      aria-label={label}
      className="mobile-search-island navigation-glass pointer-events-auto inline-flex items-center justify-center rounded-full text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-text"
      onClick={actions.length === 1 ? actions[0].onClick : undefined}
    >
      <Plus className="size-6" aria-hidden />
    </button>
  );
  if (actions.length === 1) return trigger;
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal forceMount>
        <AnimatePresence>
          {open ? (
            <Dialog.Overlay key="scrim" forceMount asChild>
              <motion.div
                key="scrim"
                className="fixed inset-0 z-40 bg-scrim"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reducedMotion ? 0 : 0.22 }}
              />
            </Dialog.Overlay>
          ) : null}
          {open ? (
            <Dialog.Content
              key="panel"
              forceMount
              asChild
              aria-describedby={undefined}
            >
              <motion.div
                key="panel"
                className="mobile-create-panel navigation-glass navigation-glass-menu fixed z-50 overflow-hidden rounded-xl text-foreground"
                initial={{
                  clipPath:
                    'inset(calc(100% - var(--mobile-tab-island-height)) 0 0 calc(100% - var(--mobile-tab-island-height)) round 999px)'
                }}
                animate={{ clipPath: 'inset(0% 0% 0% 0% round 24px)' }}
                exit={{
                  clipPath:
                    'inset(calc(100% - var(--mobile-tab-island-height)) 0 0 calc(100% - var(--mobile-tab-island-height)) round 999px)'
                }}
                transition={{
                  duration: reducedMotion ? 0 : 0.22,
                  ease: 'easeOut'
                }}
              >
                <div className="flex items-center justify-between px-4 pt-2">
                  <Dialog.Title className="text-caption text-muted-foreground">
                    {t('add-create')}
                  </Dialog.Title>
                  <Dialog.Close
                    className="inline-flex size-11 items-center justify-center rounded-full"
                    aria-label={t('close')}
                  >
                    <X className="size-5" />
                  </Dialog.Close>
                </div>
                <div className="max-h-[60dvh] overflow-y-auto p-2">
                  {actions.map(action => {
                    const Icon =
                      (
                        {
                          createChannel: Hash,
                          createDirectMessage: MessageSquare,
                          createBeverageMenu: Martini,
                          createFoodMenu: Utensils
                        } as Record<string, typeof Plus>
                      )[action.id] ?? Plus;
                    return (
                      <Dialog.Close key={action.id} asChild>
                        <button
                          type="button"
                          className="flex min-h-16 w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-body font-medium hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-text"
                          onClick={action.onClick}
                        >
                          <Icon className="size-5 shrink-0" aria-hidden />
                          {action.label}
                        </button>
                      </Dialog.Close>
                    );
                  })}
                </div>
              </motion.div>
            </Dialog.Content>
          ) : null}
        </AnimatePresence>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
